import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { startOfDay, endOfDay, subDays, subMonths } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import transactionService from '../services/transactionService';
import budgetService from '../services/budgetService';
import { useStore } from '../context/StoreContext';
import { useConstants } from '../context/ConstantsContext';
import ReceiptModal from '../components/checkout/ReceiptModal';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { formatInStoreTimezone } from '../utils/dateUtils';
import useStoreTimezone from '../hooks/useStoreTimezone';
import { isWebApp } from '../utils/platform';

const formatCurrencyAmount = (value, currencySymbol = '$') =>
  `${currencySymbol}${Number(value || 0).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState('');
  const { activeStore } = useStore();
  const { constants } = useConstants();
  const timezone = useStoreTimezone();
  const [filters, setFilters] = useState({
    period: 'today',
    startDate: '',
    endDate: '',
    paymentMethod: '',
  });
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 25 });
  const [summary, setSummary] = useState({ totalTransactions: 0, totalSalesAmount: 0, totalRefundAmount: 0, netAmount: 0 });
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetError, setBudgetError] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailState, setDetailState] = useState({
    open: false,
    loading: false,
    data: null,
    error: '',
  });
  const [receiptToPrint, setReceiptToPrint] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [exporting, setExporting] = useState(false);
  const currencySymbol = constants?.currencySymbol || '$';

  const resolveDateRange = useCallback(
    (currentFilters) => {
      const tz = timezone || 'Australia/Melbourne';
      if (currentFilters.startDate && currentFilters.endDate) {
        const startUtc = fromZonedTime(`${currentFilters.startDate}T00:00:00.000`, tz).toISOString();
        const endUtc = fromZonedTime(`${currentFilters.endDate}T23:59:59.999`, tz).toISOString();
        return { startDate: startUtc, endDate: endUtc };
      }
      const period = currentFilters.period || 'today';
      const zonedNow = toZonedTime(new Date(), tz);
      let zonedStart = zonedNow;
      if (period === 'week') {
        zonedStart = subDays(zonedNow, 7);
      } else if (period === 'month') {
        zonedStart = subMonths(zonedNow, 1);
      }
      const startUtc = fromZonedTime(startOfDay(zonedStart), tz).toISOString();
      const endUtc = fromZonedTime(endOfDay(zonedNow), tz).toISOString();
      return { startDate: startUtc, endDate: endUtc };
    },
    [timezone]
  );

  const budgetDateRange = useMemo(
    () =>
      resolveDateRange({
        period: filters.period,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }),
    [filters.period, filters.startDate, filters.endDate, resolveDateRange]
  );

  const fetchTransactions = useCallback(() => {
    if (!activeStore) {
      setTransactions([]);
      setPagination({ page: 1, totalPages: 1, total: 0, limit });
      return;
    }
    setLoading(true);
    setError('');
    const { startDate, endDate } = resolveDateRange(filters);
    transactionService
      .getTransactions({
        ...filters,
        page,
        limit,
        startDate,
        endDate,
        storeId: activeStore._id,
      })
      .then((res) => {
        setTransactions(res.data || []);
        setPagination(res.pagination || { page, totalPages: 1, total: res.data?.length || 0, limit });
        setSummary(res.summary || { totalTransactions: 0, totalSalesAmount: 0, totalRefundAmount: 0, netAmount: 0 });
      })
      .catch(() => setError('Failed to fetch transactions.'))
      .finally(() => setLoading(false));
  }, [activeStore, filters, page, limit, resolveDateRange]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    if (!activeStore) {
      setBudgetSummary(null);
      setBudgetError('');
      setBudgetLoading(false);
      return undefined;
    }

    let isCurrent = true;
    setBudgetLoading(true);
    setBudgetError('');

    budgetService
      .getPosSummary({
        storeId: activeStore._id,
        startDate: budgetDateRange.startDate,
        endDate: budgetDateRange.endDate,
      })
      .then((response) => {
        if (!isCurrent) return;
        setBudgetSummary(response.data?.data || null);
      })
      .catch((err) => {
        if (!isCurrent) return;
        const status = err?.response?.status;
        setBudgetSummary(null);
        setBudgetError(status === 403 ? 'Budget not available for this user.' : 'Failed to load budget.');
      })
      .finally(() => {
        if (isCurrent) setBudgetLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [activeStore, budgetDateRange]);

  const handlePaymentMethodChange = (value) => {
    setFilters((prev) => ({ ...prev, paymentMethod: value }));
    setPage(1);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.totalPages || 1)) return;
    setPage(nextPage);
  };

  const handleOpenDetail = (tx) => {
    if (!tx) return;
    setSelectedTransaction(tx);
    setDetailState({ open: true, loading: true, data: null, error: '' });
    transactionService
      .getTransactionDetail(tx._id, tx.type)
      .then((res) => {
        setDetailState({ open: true, loading: false, data: res.data || null, error: '' });
      })
      .catch((err) => {
        const message = err?.response?.data?.message || 'Unable to load receipt details.';
        setDetailState({ open: true, loading: false, data: null, error: message });
      });
  };

  const handleCloseDetail = () => {
    setDetailState({ open: false, loading: false, data: null, error: '' });
    setSelectedTransaction(null);
    setReceiptToPrint(null);
  };

  const handlePrintReceipt = () => {
    if (!detailState.data || detailState.loading) return;
    if (detailState.data.type !== 'Sale') return;
    setReceiptToPrint(detailState.data);
  };

  const handleExportCsv = async () => {
    if (!activeStore || exporting) return;
    setExporting(true);
    setError('');
    try {
      const { startDate, endDate } = resolveDateRange(filters);
      const baseParams = {
        ...filters,
        startDate,
        endDate,
        storeId: activeStore._id,
      };
      const maxLimit = 200;
      const firstResponse = await transactionService.getTransactions({
        ...baseParams,
        page: 1,
        limit: maxLimit,
      });
      const allTransactions = [...(firstResponse.data || [])];
      const totalPages = firstResponse.pagination?.totalPages || 1;

      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        const responses = await Promise.all(
          remainingPages.map((nextPage) =>
            transactionService.getTransactions({
              ...baseParams,
              page: nextPage,
              limit: maxLimit,
            })
          )
        );
        responses.forEach((response) => {
          allTransactions.push(...(response.data || []));
        });
      }

      const headers = [
        'Date',
        'Type',
        'Receipt Number',
        'Customer',
        'Cashier',
        'Payment Method',
        'Amount',
      ];

      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (/[",\n]/.test(stringValue)) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const rows = allTransactions.map((tx) => [
        escapeCsv(formatInStoreTimezone(tx.date, 'dd-MM-yyyy hh:mm a', timezone)),
        escapeCsv(tx.type),
        escapeCsv(tx.receiptNumber),
        escapeCsv(tx.customerName),
        escapeCsv(tx.cashierName),
        escapeCsv(tx.paymentMethod),
        escapeCsv(Number(tx.totalAmount || 0).toFixed(2)),
      ]);

      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeStore = (activeStore?.name || 'store').replace(/[^a-z0-9-_]+/gi, '-');
      const timestamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `transactions-${safeStore}-${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export transactions.');
    } finally {
      setExporting(false);
    }
  };

  const renderBudgetCard = () => {
    const coverage = budgetSummary?.coverage;
    const hasBudget = budgetSummary && coverage?.daysCovered > 0;
    const isComplete = Boolean(coverage?.isComplete);
    const progress = hasBudget && budgetSummary.budget > 0
      ? Math.min(Math.max(Number(budgetSummary.achievementPct || 0), 0), 100)
      : 0;
    const planLabel = budgetSummary?.plans?.length
      ? budgetSummary.plans.map((plan) => `${plan.name} v${plan.version}`).join(', ')
      : '';

    return (
      <div className="bg-white rounded-lg shadow p-4 sm:col-span-2 lg:col-span-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase text-gray-500 tracking-wide">Budget</p>
            <p className="text-2xl font-semibold text-gray-900 mt-2">
              {budgetLoading ? 'Loading...' : hasBudget ? formatCurrencyAmount(budgetSummary.budget, currencySymbol) : 'Not set'}
            </p>
          </div>
          {hasBudget && (
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${budgetSummary.variance >= 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {budgetSummary.achievementPct === null ? 'N/A' : `${Number(budgetSummary.achievementPct).toFixed(1)}%`}
            </span>
          )}
        </div>
        {hasBudget && (
          <>
            <div className="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
              <div className={`h-full ${budgetSummary.variance >= 0 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${progress}%` }} />
            </div>
            <p className="mt-2 text-sm text-gray-700">
              Actual {formatCurrencyAmount(budgetSummary.actual, currencySymbol)} · {budgetSummary.variance >= 0 ? 'Above' : 'Remaining'} {formatCurrencyAmount(budgetSummary.variance >= 0 ? budgetSummary.aboveTarget : budgetSummary.remaining, currencySymbol)}
            </p>
          </>
        )}
        {!budgetLoading && budgetError && <p className="mt-2 text-sm text-amber-700">{budgetError}</p>}
        {!budgetLoading && hasBudget && !isComplete && (
          <p className="mt-2 text-sm text-amber-700">
            Partial budget coverage: {coverage.daysCovered} of {coverage.totalDays} days.
          </p>
        )}
        {!budgetLoading && hasBudget && planLabel && (
          <p className="mt-2 text-xs text-gray-500 truncate" title={planLabel}>
            {planLabel} · inc GST · all payment methods
          </p>
        )}
      </div>
    );
  };

  const renderPagination = () => {
    const currentPage = pagination.page || page;
    const totalPages = pagination.totalPages || 1;
    const total = pagination.total || transactions.length;
    const currentLimit = pagination.limit || limit;
    return (
      <div className="mt-4 flex flex-wrap justify-between items-center gap-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-600">
          Page {currentPage} of {totalPages} · {total} total (showing {(currentPage - 1) * currentLimit + 1}-{Math.min(currentPage * currentLimit, total)})
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          Financial Transactions: <span className="text-blue-600">{activeStore?.name}</span>
        </h1>
        <div className="flex items-center space-x-4">
          <DateRangeFilter
            selectedPeriod={filters.period}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onSelectPreset={(period, range) => {
              setFilters((prev) => ({
                ...prev,
                period,
                startDate: range.start,
                endDate: range.end,
              }));
              setPage(1);
            }}
            onApplyCustomRange={(startDate, endDate) => {
              setFilters((prev) => ({
                ...prev,
                period: 'custom',
                startDate,
                endDate,
              }));
              setPage(1);
            }}
          />
          <div>
            <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">Payment Method</label>
            <select
              id="paymentMethod"
              name="paymentMethod"
              value={filters.paymentMethod}
              onChange={(e) => handlePaymentMethodChange(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
            >
              <option value="">All</option>
              {constants && Object.values(constants.PAYMENT_METHODS).map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
          </div>
          {isWebApp() && (
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={exporting || loading}
              className="self-end px-4 py-2 bg-primary text-white rounded-md text-sm font-semibold hover:bg-primary-dark disabled:opacity-50"
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs uppercase text-gray-500 tracking-wide">Transactions</p>
          <p className="text-2xl font-semibold text-gray-900 mt-2">{summary.totalTransactions.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs uppercase text-gray-500 tracking-wide">Sales</p>
          <p className="text-2xl font-semibold text-gray-900 mt-2">
            {formatCurrencyAmount(summary.totalSalesAmount, currencySymbol)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs uppercase text-gray-500 tracking-wide">Refunds</p>
          <p className="text-2xl font-semibold text-gray-900 mt-2">
            {formatCurrencyAmount(summary.totalRefundAmount, currencySymbol)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs uppercase text-gray-500 tracking-wide">Net</p>
          <p className={`text-2xl font-semibold mt-2 ${summary.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrencyAmount(summary.netAmount, currencySymbol)}
          </p>
        </div>
        {renderBudgetCard()}
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {renderPagination()}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">Date & Time</th>
              <th className="px-6 py-3 text-left">Type</th>
              <th className="px-6 py-3 text-left">Receipt #</th>
              <th className="px-6 py-3 text-left">Customer</th>
              <th className="px-6 py-3 text-left">Cashier</th>
              <th className="px-6 py-3 text-left">Terminal</th>
              <th className="px-6 py-3 text-left">Payment Method</th> {/* Add column */}
              <th className="px-6 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transactions.length === 0 && !loading && (
              <tr><td colSpan="8" className="text-center py-6 text-gray-500">No transactions found.</td></tr>
            )}
            {transactions.map((tx) => (
              <tr key={tx._id} className={tx.type === 'Refund' ? 'bg-red-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {formatInStoreTimezone(tx.date, 'dd-MM-yyyy hh:mm a', timezone)}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${tx.type === 'Sale' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {tx.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                  <button
                    type="button"
                    onClick={() => handleOpenDetail(tx)}
                    className="text-primary hover:text-primary-dark hover:underline"
                  >
                    {tx.receiptNumber}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{tx.customerName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{tx.cashierName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{tx.terminalName || '—'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">{tx.paymentMethod}</td> {/* Add data */}
                <td className={`px-6 py-4 whitespace-nowrap text-right font-semibold ${tx.totalAmount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatCurrencyAmount(tx.totalAmount, currencySymbol)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {loading && <p className="mt-4 text-gray-500">Loading...</p>}
      {renderPagination()}
      {detailState.open && (
        <TransactionDetailDrawer
          transaction={selectedTransaction}
          timezone={timezone}
          state={detailState}
          onClose={handleCloseDetail}
          currencySymbol={constants?.currencySymbol || '$'}
          onPrint={handlePrintReceipt}
        />
      )}
      {receiptToPrint && (
        <ReceiptModal
          sale={receiptToPrint}
          store={activeStore}
          onClose={() => setReceiptToPrint(null)}
        />
      )}
    </div>
  );
};

export default TransactionsPage;

const TransactionDetailDrawer = ({ transaction, state, onClose, currencySymbol, timezone, onPrint }) => {
  const effectiveTimezone = timezone || 'Australia/Melbourne';
  const formatCurrency = (value) => formatCurrencyAmount(value, currencySymbol);

  const renderSaleDetails = (detail) => (
    <>
      <section className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Summary</h3>
        <dl className="grid grid-cols-2 gap-3 text-sm text-gray-700">
          <div>
            <dt className="text-gray-500">Receipt #</dt>
            <dd className="font-mono">{detail.receiptNumber}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Date</dt>
            <dd>{formatInStoreTimezone(detail.date, 'dd-MM-yyyy hh:mm a', effectiveTimezone)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Cashier</dt>
            <dd>{detail.cashierName}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Customer</dt>
            <dd>{detail.customer?.name || 'Walk-in'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Payment Method</dt>
            <dd>{detail.paymentMethod}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Payment Source</dt>
            <dd>{detail.paymentSource || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Subtotal</dt>
            <dd>{formatCurrency(detail.subtotal)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Discount</dt>
            <dd>{formatCurrency(detail.totalDiscount)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">GST</dt>
            <dd>{formatCurrency(detail.gstAmount)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Rounding</dt>
            <dd>{formatCurrency(detail.rounding)}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-gray-500">Final Amount</dt>
            <dd className="text-xl font-semibold">{formatCurrency(detail.finalAmount)}</dd>
          </div>
        </dl>
      </section>
      {detail.paymentAllocations?.length > 0 && (
        <section className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Payment Breakdown</h3>
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Leg</th>
                  <th className="px-4 py-2 text-left">Method</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {detail.paymentAllocations.map((allocation, idx) => (
                  <tr key={`${allocation.allocationId || idx}-${idx}`} className="border-t">
                    <td className="px-4 py-2">{allocation.allocationId || `Leg ${idx + 1}`}</td>
                    <td className="px-4 py-2">{allocation.method || '—'}</td>
                    <td className="px-4 py-2">{allocation.source || '—'}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(allocation.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Line Items</h3>
        {detail.items?.length ? (
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Price</th>
                  <th className="px-4 py-2 text-right">Discount</th>
                  <th className="px-4 py-2 text-right">Manual discount</th>
                  <th className="px-4 py-2 text-left">Discount reason</th>
                  <th className="px-4 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map((item, idx) => (
                  <tr key={`${item.productId || idx}-${idx}`} className="border-t">
                    <td className="px-4 py-2">{item.name}</td>
                    <td className="px-4 py-2 text-right">{item.quantity}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(item.price)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(item.discount)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(item.manualDiscount)}</td>
                    <td className="px-4 py-2">{item.manualDiscountReason?.name || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      {formatCurrency(item.quantity * item.price - item.discount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No items recorded.</p>
        )}
      </section>
    </>
  );

  const renderRefundDetails = (detail) => (
    <>
      <section className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Summary</h3>
        <dl className="grid grid-cols-2 gap-3 text-sm text-gray-700">
          <div>
            <dt className="text-gray-500">Receipt #</dt>
            <dd className="font-mono">{detail.receiptNumber}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Date</dt>
            <dd>{formatInStoreTimezone(detail.date, 'dd-MM-yyyy hh:mm a', effectiveTimezone)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Cashier</dt>
            <dd>{detail.cashierName}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Refund Amount</dt>
            <dd className="text-lg font-semibold text-red-600">
              {formatCurrency(detail.refundAmount)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Payment Method</dt>
            <dd>{detail.paymentMethod || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Payment Source</dt>
            <dd>{detail.paymentSource || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Reason</dt>
            <dd>{detail.reason || '—'}</dd>
          </div>
        </dl>
      </section>
      <section className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Original Sale</h3>
        {detail.originalSale ? (
          <dl className="grid grid-cols-2 gap-3 text-sm text-gray-700">
            <div>
              <dt className="text-gray-500">Receipt #</dt>
              <dd className="font-mono">{detail.originalSale.receiptNumber}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Date</dt>
              <dd>{formatInStoreTimezone(detail.originalSale.date, 'dd-MM-yyyy hh:mm a', effectiveTimezone)}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-gray-500">Original Amount</dt>
              <dd>{formatCurrency(detail.originalSale.finalAmount)}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-gray-500">Not linked to a sale.</p>
        )}
      </section>
      {detail.refundAllocations?.length > 0 && (
        <section className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Refund Breakdown</h3>
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Leg</th>
                  <th className="px-4 py-2 text-left">Method</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {detail.refundAllocations.map((allocation, idx) => (
                  <tr key={`${allocation.allocationId || idx}-${idx}`} className="border-t">
                    <td className="px-4 py-2">{allocation.allocationId || `Leg ${idx + 1}`}</td>
                    <td className="px-4 py-2">{allocation.method || '—'}</td>
                    <td className="px-4 py-2">{allocation.source || '—'}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(allocation.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Refunded Items</h3>
        {detail.items?.length ? (
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Original Price</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map((item, idx) => (
                  <tr key={`${item.productId || idx}-${idx}`} className="border-t">
                    <td className="px-4 py-2">{item.name}</td>
                    <td className="px-4 py-2 text-right">{item.quantity}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(item.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No line items attached.</p>
        )}
      </section>
    </>
  );

  const renderContent = () => {
    if (state.loading) {
      return <p className="text-sm text-gray-500">Loading receipt…</p>;
    }
    if (state.error) {
      return <p className="text-sm text-red-600">{state.error}</p>;
    }
    if (!state.data) {
      return <p className="text-sm text-gray-500">No details available.</p>;
    }
    return state.data.type === 'Sale'
      ? renderSaleDetails(state.data)
      : renderRefundDetails(state.data);
  };

  const handleDrawerClick = (event) => {
    event.stopPropagation();
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div
        className="absolute inset-y-0 right-0 w-full max-w-xl bg-white shadow-2xl flex flex-col"
        onClick={handleDrawerClick}
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <p className="text-xs uppercase text-gray-500">{transaction?.type}</p>
            <h2 className="text-xl font-semibold text-gray-900">
              Receipt {transaction?.receiptNumber}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {state.data?.type === 'Sale' && (
              <button
                type="button"
                onClick={onPrint}
                disabled={state.loading || !state.data}
                className="text-sm font-semibold px-3 py-1 rounded border border-blue-500 text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Print Receipt
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">{renderContent()}</div>
      </div>
    </div>
  );
};
